import { Link } from "react-router-dom";
import { Button } from "./../button";
import { Logo } from "../../Logo";
import {
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Mail,
  Phone,
  MapPin,
  Send,
  Briefcase,
  Users,
  Building2,
} from "lucide-react";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    forJobSeekers: [
      { label: "Browse Jobs", to: "/" },
      { label: "My Applications", to: "/applier/applications" },
      { label: "Saved Jobs", to: "/applier/dashboard?tab=saved" },
    ],
    forEmployers: [
      { label: "Post a Job", to: "/recruiter/post-job" },
      { label: "Recruiter Dashboard", to: "/recruiter/dashboard" },
      { label: "Applications", to: "/recruiter/applications" },
    ],
    company: [
      { label: "Contact", to: "/contact" },
      { label: "Blog", to: "/blog" },
    ],
    legal: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms of Service", to: "/terms" },
      { label: "Cookie Policy", to: "/cookie-policy" },
    ],
  };

  const LinkGroup = ({ title, icon: Icon, links }) => (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground mb-3 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-primary" />}
        {title}
      </h3>
      <ul className="space-y-2">
        {links.map((link) => (
          <li key={link.to}>
            <Link
              to={link.to}
              className="text-sm text-muted-foreground hover:text-primary transition-colors inline-block"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer className="relative w-full border-t border-white/25 dark:border-white/10 glass-footer">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent" />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-10 sm:py-12">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-10">
          {/* Brand */}
          <div className="col-span-2 space-y-4">
            <div className="flex items-center gap-3 sm:flex-col sm:items-start sm:gap-4">
              <Link to="/" className="shrink-0 group">
                <Logo className="h-8 sm:h-10 w-auto max-w-[118px] sm:max-w-[180px] transition-opacity group-hover:opacity-90" />
              </Link>
              <p className="text-xs sm:text-sm text-muted-foreground leading-snug sm:leading-relaxed flex-1 min-w-0 sm:max-w-xs">
                Trusted jobs for Ethiopian professionals. Search, apply, and track your career with Shebeka.
              </p>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {[
                { Icon: Facebook, href: "https://facebook.com/shebeka", label: "Facebook" },
                { Icon: Twitter, href: "https://twitter.com/shebeka", label: "Twitter" },
                { Icon: Linkedin, href: "https://linkedin.com/company/shebeka", label: "LinkedIn" },
                { Icon: Instagram, href: "https://instagram.com/shebeka", label: "Instagram" },
              ].map(({ Icon, href, label }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9 rounded-xl hover:text-primary hover:bg-primary/10 border border-transparent hover:border-primary/20"
                  >
                    <Icon className="w-4 h-4" />
                  </Button>
                </a>
              ))}
            </div>

            <a
              href="https://t.me/shebeka_job"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2.5 rounded-xl transition-all text-sm font-medium shadow-md shadow-primary/20"
            >
              <Send className="w-4 h-4" />
              Join Telegram Channel
            </a>
          </div>

          <LinkGroup title="Job Seekers" icon={Briefcase} links={footerLinks.forJobSeekers} />
          <LinkGroup title="Employers" icon={Building2} links={footerLinks.forEmployers} />
          <LinkGroup title="Company" icon={Users} links={footerLinks.company} />
          <LinkGroup title="Legal" links={footerLinks.legal} />
        </div>

        {/* Contact */}
        <div className="mt-8 pt-6 border-t border-white/25 dark:border-white/10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <a
              href="mailto:support@shebeka.com"
              className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors rounded-xl px-3 py-2 hover:bg-primary/5"
            >
              <Mail className="w-4 h-4 text-primary shrink-0" />
              support@shebeka.com
            </a>
            <a
              href="tel:+251962333331"
              className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors rounded-xl px-3 py-2 hover:bg-primary/5"
            >
              <Phone className="w-4 h-4 text-primary shrink-0" />
              +251 96 233 3331
            </a>
            <div className="flex items-center gap-2 text-muted-foreground px-3 py-2">
              <MapPin className="w-4 h-4 text-primary shrink-0" />
              Addis Ababa, Ethiopia
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-6 pt-6 border-t border-white/25 dark:border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <p>&copy; {currentYear} Shebeka. All rights reserved.</p>
          <p className="text-center sm:text-right">
            Built by{" "}
            <a
              href="https://allcandevcenter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Allcan Dev Center
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
